import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import DiscoveryTooltip from 'DiscoveryTooltip';
import { sortRows } from 'Utils/SortableTh';
import { uiLabel } from 'Utils/Labeling';
import { STATUS, INK, FONT_MONO } from 'Utils/styleTokens';
import { groupRowsByArea, areaNoun, areaQualifierLabel } from '../areaUtils';
import {
  entityCategory, rowNote, changeLabel, diffRowKey, isRemediable, LEVEL_PILL, ageLabel, firstSeenAt,
} from '../edlUtils';
import SortHeader from './SortHeader';

const STATUS_CHIPS = [['ALL', 'All changes'], ['RESOLVED', 'Resolved'], ['PERSISTING', 'Persisting'], ['NEW', 'New']];

const STATUS_PILL = {
  RESOLVED: { label: '✓ Resolved', bg: STATUS.success.bg, fg: STATUS.success.text },
  PERSISTING: { label: 'Persisting', bg: STATUS.warning.bg, fg: STATUS.warning.text },
  NEW: { label: '↑ New', bg: STATUS.danger.bg, fg: STATUS.danger.text },
};

const ENTITY_PILL = {
  cred: { bg: '#FDECEC', fg: STATUS.danger.text },
  payment: { bg: STATUS.orange.bg, fg: STATUS.orange.text },
  pii: { bg: STATUS.neutral.bg, fg: STATUS.neutral.text },
};

const NOTE_COLOR = { RESOLVED: STATUS.success.text, NEW: STATUS.danger.text, PERSISTING: STATUS.warning.text };

const chipClass = (on) => `edl-chip${on ? ' edl-chip--on' : ''}`;

/**
 * Below this many loaded rows every group opens by default — a small source should not make the
 * user click three times to see four findings. Above it groups start collapsed, because a
 * Facets-scale comparison (tens of thousands of rows) would otherwise mount the whole ledger.
 */
const AUTO_EXPAND_ROW_CAP = 200;

const STATUS_RANK = { NEW: 0, PERSISTING: 1, RESOLVED: 2 };
const LEVEL_RANK = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
const LEVEL_CHIPS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const UNASSIGNED = 'UNASSIGNED';
const levelOfRow = (levelByEntity, r) => levelByEntity[String(r.entity).toUpperCase()] || UNASSIGNED;

// Change column colour: more findings than before is bad (red), fewer is good (green),
// no movement keeps the column's neutral colour.
const changeColor = (r) => {
  const d = (r.countTo == null ? 0 : r.countTo) - (r.countFrom == null ? 0 : r.countFrom);
  if (d > 0) return STATUS.danger.text;
  if (d < 0) return STATUS.success.text;
  return undefined;
};

/**
 * The per-(location, entity) ledger, grouped into collapsible areas (table / folder / collection).
 *
 * Grouping is what lets someone who arrived from the overview with one area picked still reach
 * every other area without going back: each heading carries its own select-all / clear, so a
 * handoff CSV can span areas that were never part of the original pick.
 *
 * Arriving with `location.state.area` expands just that group and scrolls to it. A reload loses
 * history state, so the fallback is "all collapsed" — auto-expanding everything would render
 * tens of thousands of rows at Facets scale.
 */
const FindingsList = ({
  url, source, rows, diffError, diffUnsupported, loadingDiff, progress, toId, levelByEntity,
  selected, toggleRow, setRowsSelected, clearSelection, selectedRows, isBaseline,
  fromLabel, toLabel, fromCompletedAt, toCompletedAt,
}) => {
  const location = useLocation();
  const arrivedArea = location.state?.area;
  const [status, setStatus] = useState('ALL');
  const [level, setLevel] = useState('ALL');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'level', dir: 'asc' });
  // null = "not chosen yet", which lets the auto-expand default below apply. A Set (even empty)
  // means the user has expressed a preference and is honoured as-is.
  const [expanded, setExpanded] = useState(null);
  const areaRefs = useRef({});

  const areas = useMemo(
    () => groupRowsByArea(rows, source, levelByEntity),
    [rows, source, levelByEntity],
  );
  const noun = areaNoun(source);

  // Expand + scroll to the area the user picked on the overview. Depends on areas.length because
  // the diff may still have been paging in when this mounted, so the node isn't there on mount.
  useEffect(() => {
    if (arrivedArea == null) return;
    setExpanded(new Set([arrivedArea]));
    const node = areaRefs.current[arrivedArea];
    // jsdom has no scrollIntoView, and neither do older browsers for the options form.
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'start' });
    }
  }, [arrivedArea, areas.length]);

  const counts = useMemo(() => {
    const c = { ALL: rows.length, RESOLVED: 0, PERSISTING: 0, NEW: 0, CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, [UNASSIGNED]: 0 };
    rows.forEach((r) => { c[r.status] += 1; c[levelOfRow(levelByEntity, r)] += 1; });
    return c;
  }, [rows, levelByEntity]);

  const sortAccessors = useMemo(() => ({
    status: (r) => STATUS_RANK[r.status] ?? 9,
    location: (r) => r.location,
    entity: (r) => r.entity,
    level: (r) => LEVEL_RANK[levelByEntity[String(r.entity).toUpperCase()]] ?? null,
    change: (r) => (r.countTo == null ? 0 : r.countTo),
    age: (r) => { const d = firstSeenAt(r, fromCompletedAt, toCompletedAt); return d ? d.getTime() : null; },
  }), [levelByEntity, fromCompletedAt, toCompletedAt]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (r) => (status === 'ALL' || r.status === status)
      && (level === 'ALL' || levelOfRow(levelByEntity, r) === level)
      && (!q || r.location.toLowerCase().includes(q) || r.entity.toLowerCase().includes(q));
  }, [status, level, levelByEntity, query]);

  // Filters apply WITHIN each group; a group with nothing left to show drops out entirely.
  const visibleAreas = useMemo(() => areas
    .map((a) => ({ ...a, visibleRows: sortRows(a.rows.filter(matches), sort, sortAccessors) }))
    .filter((a) => a.visibleRows.length > 0), [areas, matches, sort, sortAccessors]);

  const shownCount = useMemo(
    () => visibleAreas.reduce((n, a) => n + a.visibleRows.length, 0),
    [visibleAreas],
  );

  // Until the user touches a caret (or arrives from the overview), open everything on a small
  // comparison and nothing on a large one.
  const effectiveExpanded = useMemo(() => {
    if (expanded) return expanded;
    return rows.length <= AUTO_EXPAND_ROW_CAP
      ? new Set(visibleAreas.map((a) => a.key))
      : new Set();
  }, [expanded, rows.length, visibleAreas]);

  const toggleArea = (key) => setExpanded(() => {
    const next = new Set(effectiveExpanded);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const expandAll = () => setExpanded(new Set(visibleAreas.map((a) => a.key)));
  const collapseAll = () => setExpanded(new Set());

  if (toId == null) {
    return (
      <div className='edl-table'>
        <div style={{ padding: '18px 20px' }} className='sg-small' data-testid='edl-no-pair'>
          A completed discovery run is needed to compare. Run discovery on this source to see its findings
          here; a second run then shows what was resolved, what persists and what is new.
        </div>
      </div>
    );
  }

  if (diffError) {
    return (
      <div className='edl-table'>
        <div
          style={{ padding: '18px 20px', color: diffUnsupported ? INK[500] : STATUS.danger.text }}
          className='sg-small'
          data-testid='edl-diff-error'
          data-unsupported={diffUnsupported ? 'true' : 'false'}
        >
          {diffError}
        </div>
      </div>
    );
  }

  return (
    <div className='edl-table'>
      <div className='edl-table__toolbar'>
        <input className='edl-search' placeholder='Search files, folders, entities…' value={query}
          onChange={(e) => setQuery(e.target.value)} data-testid='edl-search' />
        <span style={{ color: INK[200] }}>|</span>
        {STATUS_CHIPS.map(([key, label]) => (
          <button key={key} type='button' className={chipClass(status === key)} onClick={() => setStatus(key)}
            data-testid={`edl-status-${key}`}>
            {label} {counts[key]}
          </button>
        ))}
        <span style={{ color: INK[200] }}>|</span>
        <button type='button' className={chipClass(level === 'ALL')} onClick={() => setLevel('ALL')} data-testid='edl-level-ALL'>
          All levels {counts.ALL}
        </button>
        {LEVEL_CHIPS.map((key) => (
          <button key={key} type='button' className={chipClass(level === key)} onClick={() => setLevel(key)}
            data-testid={`edl-level-${key}`}
            style={level === key ? { background: LEVEL_PILL[key].fg, color: '#fff' } : { background: LEVEL_PILL[key].bg, color: LEVEL_PILL[key].fg }}>
            {LEVEL_PILL[key].label} {counts[key]}
          </button>
        ))}
        {counts[UNASSIGNED] > 0 && (
          <button type='button' className={chipClass(level === UNASSIGNED)} onClick={() => setLevel(UNASSIGNED)}
            data-testid='edl-level-UNASSIGNED' title='Entities not assigned to any landscape severity tier'>
            Unassigned {counts[UNASSIGNED]}
          </button>
        )}
      </div>

      <div className='edl-table__select'>
        <span data-testid='edl-selection-summary'>
          {selectedRows.length === 0
            ? 'Select open findings (new or persisting), then Actions → Download CSV.'
            : `${selectedRows.length.toLocaleString()} selected · Actions → Download CSV`}
        </span>
        <Link to={url} className='edl-chip' data-testid='edl-back-to-areas'>← All {noun}</Link>
        {visibleAreas.length > 0 && (
          <button type='button' className='edl-chip' onClick={expandAll} data-testid='edl-expand-all'>Expand all</button>
        )}
        {effectiveExpanded.size > 0 && (
          <button type='button' className='edl-chip' onClick={collapseAll} data-testid='edl-collapse-all'>Collapse all</button>
        )}
        {selectedRows.length > 0 && (
          <button type='button' className='edl-chip' onClick={clearSelection} data-testid='edl-select-clear'>Clear all</button>
        )}
      </div>

      <div className='edl-row--head'>
        <span aria-hidden='true' />
        <SortHeader label='Level' sortKey='level' sort={sort} onSort={setSort} title='Sort by severity level (Critical first when ascending)' />
        <SortHeader label='Location' sortKey='location' sort={sort} onSort={setSort} />
        <SortHeader label='Entity' sortKey='entity' sort={sort} onSort={setSort} />
        <SortHeader label='Change' sortKey='change' sort={sort} onSort={setSort} title='Sort by current count' />
        <SortHeader label='Age' sortKey='age' sort={sort} onSort={setSort} title='Sort by first-seen date (oldest first when ascending)' />
        <SortHeader label='Status' sortKey='status' sort={sort} onSort={setSort} />
      </div>

      {isBaseline && (
        <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--ink-100)', color: INK[500] }}
          className='sg-small' data-testid='edl-baseline-note'>
          Only one completed discovery run — comparing {toLabel} against a baseline where every count is zero,
          so every finding is new. Run discovery again to see what was resolved or persists.
        </div>
      )}

      {loadingDiff && (
        <div style={{ padding: '18px 20px' }} className='sg-small' data-testid='edl-list-loading'>
          {progress && progress.total
            ? `Loading changes… ${progress.loaded.toLocaleString()} of ${progress.total.toLocaleString()}`
            : 'Loading changes…'}
        </div>
      )}

      {!loadingDiff && visibleAreas.length === 0 && (
        <div style={{ padding: '18px 20px' }} className='sg-small'>No changes match the current filters.</div>
      )}

      {visibleAreas.map((area) => {
        const isOpen = effectiveExpanded.has(area.key);
        const openHere = area.visibleRows.filter(isRemediable);
        const allPicked = openHere.length > 0 && openHere.every((r) => selected.has(diffRowKey(r)));
        return (
          <div key={area.key || '(root)'} data-testid='edl-area-group' data-area={area.key}
            ref={(el) => { areaRefs.current[area.key] = el; }}>
            <div className='edl-area-group'>
              <button
                type='button'
                className='edl-area-group__toggle'
                aria-expanded={isOpen}
                onClick={() => toggleArea(area.key)}
                data-testid='edl-area-toggle'
              >
                <span className='edl-area-group__caret' aria-hidden='true'>{isOpen ? '▾' : '▸'}</span>
                {/* Table (or collection / folder) name, with the schema as a quieter suffix rather
                    than glued on in front as "schema.table_name". */}
                <bdi className='edl-area-group__name'>{area.name || area.label}</bdi>
                {areaQualifierLabel(area) && (
                  <bdi className='edl-area-group__qual' data-testid='edl-area-group-qualifier'>
                    {areaQualifierLabel(area)}
                  </bdi>
                )}
              </button>
              <span className='sg-small' style={{ color: INK[500] }} data-testid='edl-area-group-counts'>
                {area.visibleRows.length.toLocaleString()} shown
                {area.new > 0 && <span style={{ color: STATUS.danger.text }}> · {area.new.toLocaleString()} new</span>}
              </span>
              {openHere.length > 0 && (
                <button
                  type='button'
                  className='edl-chip'
                  onClick={() => setRowsSelected(openHere, !allPicked)}
                  data-testid='edl-area-select-all'
                  title={allPicked
                    ? `Deselect the open findings in ${area.label}`
                    : `Select the ${openHere.length.toLocaleString()} open finding${openHere.length === 1 ? '' : 's'} in ${area.label}`}
                >
                  {allPicked ? 'Deselect all' : `Select all (${openHere.length.toLocaleString()})`}
                </button>
              )}
            </div>

            {isOpen && area.visibleRows.map((r) => {
              const pill = STATUS_PILL[r.status] || STATUS_PILL.PERSISTING;
              const ent = ENTITY_PILL[entityCategory(r.entity)];
              const remediable = isRemediable(r);
              const isSelected = selected.has(diffRowKey(r));
              return (
                <div className={`edl-row${isSelected ? ' edl-row--selected' : ''}`} key={diffRowKey(r)}
                  data-testid='edl-diff-row' data-status={r.status} data-selected={isSelected}>
                  <button
                    type='button'
                    role='checkbox'
                    aria-checked={isSelected}
                    aria-label={remediable ? `Select ${r.entity} at ${r.location}` : 'Resolved — nothing to hand off'}
                    title={remediable ? undefined : 'Resolved findings are already gone and are not handed off'}
                    className={`edl-checkbox${isSelected ? ' edl-checkbox--on' : ''}${remediable ? '' : ' edl-checkbox--off'}`}
                    disabled={!remediable}
                    onClick={() => toggleRow(r)}
                    data-testid='edl-row-check'
                  >
                    {isSelected ? '✓' : ''}
                  </button>
                  {(() => {
                    const lvl = LEVEL_PILL[levelByEntity[String(r.entity).toUpperCase()]];
                    return lvl ? (
                      <span className='sg-severity' style={{ background: lvl.bg, color: lvl.fg, justifySelf: 'start' }} data-testid='edl-row-level'>{lvl.label}</span>
                    ) : (
                      <span className='edl-row__spans' data-testid='edl-row-level' title='Not assigned to a landscape severity tier'>—</span>
                    );
                  })()}
                  <div style={{ minWidth: 0 }}>
                    <DiscoveryTooltip placement='top' text={<span style={{ fontFamily: FONT_MONO, wordBreak: 'break-all' }}>{r.location}</span>}>
                      <span className={`edl-row__loc${r.status === 'RESOLVED' ? ' edl-row__loc--resolved' : ''}`} data-testid='edl-row-loc'><bdi>{r.location}</bdi></span>
                    </DiscoveryTooltip>
                  </div>
                  <span className='edl-row__ent' style={{ background: ent.bg, color: ent.fg }} title={uiLabel(r.entity)}>{r.entity}</span>
                  <span className='edl-row__spans' data-testid='edl-row-change' style={{ color: changeColor(r) }}>{changeLabel(r)}</span>
                  <span className='edl-row__note' style={{ color: NOTE_COLOR[r.status] }} data-testid='edl-row-age'
                    title={`${rowNote(r, fromLabel, toLabel)}${r.status === 'NEW' ? '' : ' · present since at least the earlier run'}`}>
                    {ageLabel(r, fromCompletedAt, toCompletedAt)}
                  </span>
                  <span className='sg-pill sg-pill--nodot' style={{ background: pill.bg, color: pill.fg, justifySelf: 'start', whiteSpace: 'nowrap' }}>
                    {pill.label}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}

      {!loadingDiff && (
        <div style={{ padding: '12px 20px', color: INK[400] }} className='sg-small'>
          {shownCount.toLocaleString()} of {rows.length.toLocaleString()} changes across {visibleAreas.length.toLocaleString()} {noun}
        </div>
      )}
    </div>
  );
};

export default FindingsList;
